// Third party
import React from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase';

// Namespaces
import * as ProposalNS from '../../../Proposal';

// Components
import { Checkbox, Radio, ToggleSwitch } from '../../../../../views/components/inputs';
import { CollapsibleContainer } from '../../../../../views/components/misc';
import { InfoTooltip } from '../../../../../views/components/tooltips';

// Utils
import { formatLocalizedString, formatMessage } from '../../../../../utils/i18n';
import { userHasPermission, userHasPermissions } from '../../../../../utils/permissions';
import { validateRiskProfileBuildRules, validateDisableRules } from '../../../ProposalHelpers';
import { isFFEnabled } from '@/utils/feature-flag';
import { getPortfoliosForView } from '../../../utils/client-level-summary';

import { IconButton } from '@/views/components/buttons';
import User from '@/types/user';

type Props = {
  user: User;
  className?: string;
  proposal: ProposalNS.Proposal;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  selectedSections: string[];
  selectedDivisions: string[];
  onJumpToPage: (section: string) => void;
  pageIndexes?: ProposalNS.ProposalSectionPageMapping;
};

class ProposalSections extends React.PureComponent<Props> {
  sectionIsSelected = (sectionKey: string): boolean => this.props.selectedSections.indexOf(sectionKey) > -1;

  divisionIsSelected = (divisionKey: string): boolean => this.props.selectedDivisions.indexOf(divisionKey) > -1;

  runBuildRules = (buildRules: ProposalNS.ElementBuildRule[]): boolean => {
    const { proposal, user } = this.props;

    if (buildRules !== undefined) {
      for (const rule of buildRules) {
        switch (rule.type) {
          case ProposalNS.ElementBuildRuleTypes.ItemType: {
            const hasItemOfTypes = proposal.items.some(item => rule.itemTypes.some(itemType => itemType === item.type));
            if (!hasItemOfTypes) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.ItemProperty: {
            const hasItemWithProperty = proposal.items.some(item => Object.prototype.hasOwnProperty.call(item, rule.property));
            if (!hasItemWithProperty) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.ProposalProperty: {
            const value = get(proposal, rule.property);

            if (value == null) {
              return false;
            }

            break;
          }
          case ProposalNS.ElementBuildRuleTypes.RiskProfile: {
            if (!validateRiskProfileBuildRules(proposal, rule)) {
              return false;
            }

            break;
          }
          case ProposalNS.ElementBuildRuleTypes.Permission: {
            if (rule.operator !== undefined && rule.operator === 'OR') {
              if (!rule.permissions.some(permission => userHasPermission(user, permission))) {
                return false;
              }
            } else if (!userHasPermissions(user, rule.permissions)) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.FeatureFlag: {
            if (rule.operator === 'OR') {
              if (!rule.flags.some(flag => isFFEnabled(flag))) {
                return false;
              }
            } else if (!rule.flags.every(flag => isFFEnabled(flag))) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.Orientation: {
            const { orientation } = proposal.options;
            if (orientation !== rule.orientation) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.ItemPurpose: {
            const hasItemWithPurpose = proposal.items.some(item => item.flags?.includes(rule.purpose));
            if (!hasItemWithPurpose) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.GIFItems: {
            if (!proposal.gifItems || proposal.gifItems.length === 0) {
              return false;
            }
            break;
          }
          case ProposalNS.ElementBuildRuleTypes.AccountSetPortfolios: {
            const portfolios = getPortfoliosForView(proposal, rule.view);

            if (portfolios.length === 0) {
              return false;
            }

            break;
          }
        }
      }
    }

    return true;
  };

  formatTooltipMessage = (sectionKey: string, pageIndexes?: ProposalNS.ProposalSectionPageMapping) => {
    if (pageIndexes && pageIndexes[sectionKey]) {
      const { start, end } = pageIndexes[sectionKey];
      const count = end ? start - end : 0;
      return formatMessage('Proposal.PageIndex', { count, start, end: end || 0 });
    }

    return '';
  };

  toggleSection(section: string, selected: boolean) {
    const { onToggleSection } = this.props;

    onToggleSection(section, selected);
  }

  toggleDivision(division: string, selected: boolean) {
    const { onToggleDivision } = this.props;

    onToggleDivision(division, selected);
  }

  renderProposalSection = (section: ProposalNS.ProposalTemplateSectionElement, divisionDisabled?: boolean) => {
    const { buildRules } = section;
    const { selectedSections, proposal, pageIndexes, onJumpToPage } = this.props;

    if (buildRules !== undefined && !this.runBuildRules(buildRules)) {
      return null;
    }

    let isDisabled = divisionDisabled;
    let sidebarTooltip;

    if (!isDisabled) {
      const validation =
        section.disableRules == null
          ? { isDisabled: false, sidebarTooltip: undefined }
          : validateDisableRules(section.disableRules, selectedSections, proposal.items, proposal.riskProfile);

      isDisabled = validation.isDisabled;
      sidebarTooltip = validation.sidebarTooltip;
    }

    switch (section.sidebarControl) {
      case ProposalNS.ProposalSidebarControl.Toggle: {
        return (
          <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
            <ToggleSwitch
              checked={!isDisabled && this.sectionIsSelected(section.key)}
              onChange={checked => this.toggleSection(section.key, checked)}
              disabled={isDisabled}
            >
              <>
                <span className="label">{formatLocalizedString(section.label)}</span>

                {section.sidebarTooltip && (
                  <InfoTooltip title={formatLocalizedString(section.sidebarTooltip.title)} message={formatLocalizedString(section.sidebarTooltip.message)} />
                )}
              </>
            </ToggleSwitch>
          </div>
        );
      }

      case ProposalNS.ProposalSidebarControl.Radio:
        return (
          <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
            <Radio
              selected={this.sectionIsSelected(section.key)}
              onChange={checked => this.toggleSection(section.key, checked)}
              label={formatLocalizedString(section.label)}
              disabled={isDisabled}
            />
            {pageIndexes && pageIndexes[section.key] && (
              <div className="jump-to-page-button">
                <IconButton icon="jump-to" tooltip={formatMessage('Proposal.JumpToPage')} onClick={() => onJumpToPage(section.key)} />
              </div>
            )}
          </div>
        );

      case ProposalNS.ProposalSidebarControl.Checkbox: {
        let output;

        const checkbox = (
          <Checkbox
            selected={!isDisabled && this.sectionIsSelected(section.key)}
            onChange={checked => this.toggleSection(section.key, checked)}
            label={formatLocalizedString(section.label)}
            disabled={isDisabled}
          />
        );

        if (isDisabled && sidebarTooltip) {
          output = (
            <div className="d-flex align-items-center">
              {checkbox}
              <InfoTooltip place="right" title={formatLocalizedString(sidebarTooltip.title)} message={formatLocalizedString(sidebarTooltip.message)} />
            </div>
          );
        } else {
          output = checkbox;
        }

        return (
          <div key={`section ${section.key}`} className={classNames('proposal-checklist-option', { disabled: isDisabled })}>
            {output}

            {pageIndexes && pageIndexes[section.key] && (
              <div className="jump-to-page-button">
                <IconButton
                  className="jump-to-page-button"
                  icon="jump-to"
                  tooltip={formatMessage('Proposal.JumpToPage')}
                  onClick={() => onJumpToPage(section.key)}
                />
              </div>
            )}
          </div>
        );
      }

      case ProposalNS.ProposalSidebarControl.None:
        return (
          <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
            <span className="label">{formatLocalizedString(section.label)}</span>
          </div>
        );

      default:
        return null;
    }
  };

  renderProposalDivisionTitle = (division: ProposalNS.ProposalTemplateDivisionElement, disabled?: boolean, sidebarTooltip?: ProposalNS.SidebarTooltip) => {
    if (division.sidebarControl === ProposalNS.ProposalSidebarControl.Checkbox) {
      return (
        <>
          <Checkbox
            selected={this.divisionIsSelected(division.key)}
            onChange={checked => this.toggleDivision(division.key, checked)}
            label={formatLocalizedString(division.label)}
            disabled={disabled}
          />

          {sidebarTooltip && <InfoTooltip title={formatLocalizedString(sidebarTooltip.title)} message={formatLocalizedString(sidebarTooltip.message)} />}
        </>
      );
    }

    return (
      <h3 className="proposal-division-title">
        {formatLocalizedString(division.label)}
        {sidebarTooltip && <InfoTooltip title={formatLocalizedString(sidebarTooltip.title)} message={formatLocalizedString(sidebarTooltip.message)} />}
      </h3>
    );
  };

  renderProposalDivision = (division: ProposalNS.ProposalTemplateDivisionElement) => {
    const { proposal, selectedDivisions } = this.props;
    const { buildRules } = division;

    if (buildRules !== undefined && !this.runBuildRules(buildRules)) {
      return null;
    }

    const { isDisabled, sidebarTooltip } =
      division.disableRules == null
        ? { isDisabled: false, sidebarTooltip: undefined }
        : validateDisableRules(division.disableRules, selectedDivisions, proposal.items, proposal.riskProfile);

    if (!division.sections.some(section => section.sidebarControl !== ProposalNS.ProposalSidebarControl.Hide)) {
      return null;
    }

    const showDivider = division.showDivider === undefined || division.showDivider === true ? 'proposal-division-divider' : '';
    return (
      <div className={classNames(`proposal-division-option ${showDivider}`)} key={`division ${division.key}`}>
        {(division.showTitle === undefined || division.showTitle === true) && this.renderProposalDivisionTitle(division, isDisabled, sidebarTooltip)}

        {division.sections.map((section, index) => (
          <React.Fragment key={`${section.key}-${index}`}>{this.renderProposalSection(section, isDisabled)}</React.Fragment>
        ))}
      </div>
    );
  };

  render() {
    const { className, proposal } = this.props;
    const { root } = proposal.template;

    return (
      <CollapsibleContainer className={classNames('proposal-sections', className)} headerText={formatMessage('Proposal.Tabs.Customize.Title')} headerTag="h2">
        {root.map(division => this.renderProposalDivision(division))}
      </CollapsibleContainer>
    );
  }
}

export default ProposalSections;
